import React from "react";
import { X, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import { useTranslation } from "react-i18next";

const STATUS_CONFIG = {
    passed: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
    failed: { icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
    error: { icon: AlertCircle, color: "text-orange-600", bg: "bg-orange-50" },
};

function TestingResultModal({ testRun, onClose }) {
    const { t } = useTranslation();
    const summary = testRun.summary || { total: 0, passed: 0, failed: 0 };
    const results = testRun.results || [];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="text-lg font-semibold">
                        {t("testing.testRunResults") || "Test Run Results"}
                    </h3>
                    <Button variant="ghost" size="sm" onClick={onClose}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>
                <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600 font-medium">
                            {summary.passed} {t("testing.passed") || "passed"}
                        </span>
                        <span className="text-red-600 font-medium">
                            {summary.failed} {t("testing.failed") || "failed"}
                        </span>
                        <span className="text-gray-500">
                            {summary.total} {t("testing.total") || "total"}
                        </span>
                        {testRun.duration && (
                            <span className="text-gray-400">
                                {(testRun.duration / 1000).toFixed(1)}s
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-4 space-y-3">
                    {results.map((result) => {
                        const cfg = STATUS_CONFIG[result.status] || STATUS_CONFIG.error;
                        const Icon = cfg.icon;
                        return (
                            <div
                                key={result.id}
                                className={`rounded-md border p-3 ${cfg.bg}`}
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                                    <span className="text-sm font-medium">
                                        {result.testCase?.name || result.testCaseId}
                                    </span>
                                    {result.duration && (
                                        <span className="text-xs text-gray-400 ml-auto">
                                            {(result.duration / 1000).toFixed(1)}s
                                        </span>
                                    )}
                                </div>
                                {result.error && (
                                    <p className="text-xs text-red-600 mt-1">
                                        {result.error}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    {results.length === 0 && (
                        <p className="text-sm text-gray-500 text-center py-4">
                            {t("testing.noResults") || "No results yet."}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default TestingResultModal;
